import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Sparkles,
  ArrowLeft,
  Loader2,
  User,
  Users,
  MapPin,
  Briefcase,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  SlidersHorizontal,
  Globe,
  Crosshair,
  Euro,
  Star,
  X,
  ThumbsUp,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useMatchingSearch } from '../hooks/useMatching';
import { useCandidates } from '../hooks/useCandidates';
import { FavoriteButton } from '../components/FavoriteButton';
import { rescore, DEFAULT_WEIGHTS, CRITERIA_LABELS, ALL_CRITERIA_LABELS, getLocationWeight, getBudgetWeight } from '../utils/rescore';
import type { BudgetTolerance, LocationMode } from '../utils/rescore';
import type {
  CriteriaWeights,
  ExtractedCriteria,
  MatchingResultWithScore,
} from '../types/matching.types';

const BUDGET_TOLERANCE_OPTIONS: { value: BudgetTolerance; label: string }[] = [
  { value: 'strict', label: 'Strict' },
  { value: '5', label: '± 5%' },
  { value: '10', label: '± 10%' },
  { value: '20', label: '± 20%' },
  { value: 'flexible', label: 'Flexible' },
];

export function SemanticSearchPage() {
  const [query, setQuery] = useState('');
  const [weights, setWeights] = useState<CriteriaWeights>({ ...DEFAULT_WEIGHTS });
  const [showSliders, setShowSliders] = useState(true);
  const [locationMode, setLocationMode] = useState<LocationMode>('none');
  const [budgetTolerance, setBudgetTolerance] = useState<BudgetTolerance>('10');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([]);

  const { mutate, data, isPending, error, reset } = useMatchingSearch();

  // Résultats rescorés avec les poids courants + mode localisation + tolérance budget
  const scoredResults = useMemo<MatchingResultWithScore[]>(() => {
    if (!data?.results) return [];
    return rescore(data.results, weights, locationMode, budgetTolerance);
  }, [data?.results, weights, locationMode, budgetTolerance]);

  // Deux listes : les favoris priment sur les autres (chacune triée par score)
  const favoriteResults = useMemo(
    () => scoredResults.filter((r) => r.isFavorite),
    [scoredResults],
  );
  const otherResults = useMemo(
    () => scoredResults.filter((r) => !r.isFavorite),
    [scoredResults],
  );

  // Critères exclus de la note (poids 0) → barres masquées dans les résultats
  const hiddenCriteria = useMemo<Set<string>>(() => {
    const hidden = new Set<string>();
    if (getLocationWeight(locationMode) === 0) hidden.add('localisation');
    if (getBudgetWeight(budgetTolerance) === 0) hidden.add('budget');
    return hidden;
  }, [locationMode, budgetTolerance]);

  const launchMatching = useCallback(
    (rawInput: string) => {
      reset();
      mutate({
        rawInput,
        locationMode,
        budgetTolerance,
        candidateIds: selectedCandidateIds.length > 0 ? selectedCandidateIds : undefined,
      });
    },
    [reset, mutate, locationMode, budgetTolerance, selectedCandidateIds],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length >= 10) {
      launchMatching(query.trim());
    }
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
    launchMatching(example);
  };

  const toggleCandidateSelection = useCallback((id: number) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleWeightChange = useCallback((key: keyof CriteriaWeights, value: number) => {
    setWeights((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetWeights = useCallback(() => {
    setWeights({ ...DEFAULT_WEIGHTS });
    setLocationMode('none');
    setBudgetTolerance('10');
  }, []);

  const totalTokens = data?.usage
    ? data.usage.extractionTokens + data.usage.scoringTokens + data.usage.embeddingTokens
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/itc/candidates">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux candidats
          </Button>
        </Link>
      </div>

      {/* Title */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          Matching IA
        </h1>
        <p className="text-muted-foreground mt-2">
          Décrivez un besoin client en langage naturel. L'IA extrait les critères,
          recherche les candidats et les score selon 6 dimensions.
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle>Décrivez le besoin client</CardTitle>
          <CardDescription>
            Minimum 10 caractères. Incluez le poste, les compétences, la localisation, l'expérience...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ex: Développeur React senior avec expérience e-commerce, basé en Île-de-France...

Vous pouvez coller une annonce complète ici."
                className="pl-10 min-h-[120px] resize-y"
                rows={5}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={isPending || query.trim().length < 10}>
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span className="ml-2">{isPending ? 'Analyse...' : 'Lancer le matching'}</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Examples (shown when no results) */}
      {!data && !isPending && (
        <Card>
          <CardHeader>
            <CardTitle>Exemples de recherches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[
                'Développeur fullstack JavaScript React Node.js senior à Paris',
                'Data scientist Python machine learning 5 ans d\'expérience',
                'DevOps AWS Kubernetes CI/CD senior en Île-de-France',
                'Chef de projet agile Scrum avec background technique secteur banque',
                'Architecte cloud Azure microservices secteur santé Lyon',
              ].map((example) => (
                <Button
                  key={example}
                  variant="outline"
                  size="sm"
                  onClick={() => handleExampleClick(example)}
                >
                  {example}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              Erreur lors du matching : {error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Extracted Criteria */}
      {data?.criteria && (
        <CriteriaDisplay criteria={data.criteria} />
      )}

      {/* Search Parameters — toujours visible */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => setShowSliders(!showSliders)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5" />
              <CardTitle>Paramètres de recherche</CardTitle>
            </div>
            {showSliders ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        {showSliders && (
          <CardContent className="space-y-6">
            {/* Location mode */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Localisation</span>
              </div>
              <Select value={locationMode} onValueChange={(v) => setLocationMode(v as LocationMode)}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Sans contrainte</span>
                  </SelectItem>
                  <SelectItem value="remote">
                    <span className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Full remote</span>
                  </SelectItem>
                  <SelectItem value="50km">
                    <span className="flex items-center gap-2"><Crosshair className="h-3.5 w-3.5" /> Rayon de 50 km</span>
                  </SelectItem>
                  <SelectItem value="100km">
                    <span className="flex items-center gap-2"><Crosshair className="h-3.5 w-3.5" /> Rayon de 100 km</span>
                  </SelectItem>
                  <SelectItem value="200km">
                    <span className="flex items-center gap-2"><Crosshair className="h-3.5 w-3.5" /> Rayon de 200 km</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {(locationMode === 'remote' || locationMode === 'none') && (
                <p className="text-xs text-muted-foreground">
                  La localisation est exclue de la note (barre masquée dans les résultats).
                </p>
              )}
              {locationMode !== 'none' && locationMode !== 'remote' && (
                <p className="text-xs text-muted-foreground">
                  Les candidats seront pénalisés au-delà de {locationMode.replace('km', '')} km du lieu indiqué.
                </p>
              )}
            </div>

            {/* Budget tolerance */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Tolérance budget</span>
              </div>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {BUDGET_TOLERANCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBudgetTolerance(opt.value)}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-r last:border-r-0 border-border
                      ${budgetTolerance === opt.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-muted text-foreground'
                      }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {budgetTolerance === 'strict' && 'Le budget doit correspondre exactement aux prétentions du candidat.'}
                {budgetTolerance === '5' && '± 5% de tolérance sur le budget demandé.'}
                {budgetTolerance === '10' && '± 10% de tolérance sur le budget demandé.'}
                {budgetTolerance === '20' && '± 20% de tolérance sur le budget demandé.'}
                {budgetTolerance === 'flexible' && 'Le budget est exclu de la note (barre masquée dans les résultats).'}
              </p>
            </div>

            <hr className="border-border" />

            {/* Weight sliders for 4 adjustable criteria */}
            <div className="space-y-1">
              <span className="text-sm font-medium text-muted-foreground">Poids des critères</span>
            </div>
            {(Object.keys(weights) as Array<keyof CriteriaWeights>).map((key) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{CRITERIA_LABELS[key]}</span>
                  <span className="text-sm text-muted-foreground">
                    {weights[key] === 0 ? 'Ignoré' : weights[key]}
                  </span>
                </div>
                <Slider
                  value={[weights[key]]}
                  onValueChange={([v]) => handleWeightChange(key, v!)}
                  min={0}
                  max={5}
                  step={1}
                />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={handleResetWeights}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Réinitialiser
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Restriction à une sélection de candidats */}
      <CandidateSelector
        selectedIds={selectedCandidateIds}
        onToggle={toggleCandidateSelection}
        onClear={() => setSelectedCandidateIds([])}
      />

      {/* Results */}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {scoredResults.length} résultat{scoredResults.length > 1 ? 's' : ''}
            </h2>
            {totalTokens > 0 && (
              <Badge variant="outline" className="text-xs">
                {totalTokens} tokens utilisés
              </Badge>
            )}
          </div>

          {scoredResults.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <User className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">Aucun résultat</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Aucun candidat ne correspond à votre recherche. Vérifiez que les candidats ont des embeddings.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {favoriteResults.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 fill-current text-yellow-400" />
                    <h3 className="text-sm font-semibold text-yellow-600">
                      Favoris ({favoriteResults.length})
                    </h3>
                  </div>
                  {favoriteResults.map((result, index) => (
                    <MatchResultCard key={result.candidateId} result={result} rank={index + 1} hiddenCriteria={hiddenCriteria} />
                  ))}
                </div>
              )}

              <div className="space-y-3">
                {favoriteResults.length > 0 && (
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Autres candidats ({otherResults.length})
                  </h3>
                )}
                {otherResults.map((result, index) => (
                  <MatchResultCard key={result.candidateId} result={result} rank={index + 1} hiddenCriteria={hiddenCriteria} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* How it works (shown when idle) */}
      {!data && !isPending && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <h3 className="font-medium mb-2">Comment fonctionne le matching IA ?</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>1. L'IA extrait les critères de votre texte (poste, compétences, localisation...)</li>
              <li>2. Les critères sont résolus vers notre nomenclature métier et géographique</li>
              <li>3. Les candidats sont recherchés par similarité sémantique + filtres structurés</li>
              <li>4. Chaque candidat est scoré sur 6 critères par l'IA</li>
              <li>5. Ajustez les paramètres ci-dessus pour affiner le classement instantanément</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- Sous-composants ---

function CriteriaDisplay({ criteria }: { criteria: ExtractedCriteria }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Critères extraits par l'IA</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3">
          {criteria.poste && (
            <div>
              <span className="text-xs text-muted-foreground">Poste</span>
              <Badge variant="default" className="ml-1">{criteria.poste}</Badge>
            </div>
          )}
          {criteria.competences?.length > 0 && (
            <div>
              <span className="text-xs text-muted-foreground">Compétences</span>
              {criteria.competences.map((c) => (
                <Badge key={c} variant="secondary" className="ml-1">{c}</Badge>
              ))}
            </div>
          )}
          {criteria.experienceMin != null && (
            <div>
              <span className="text-xs text-muted-foreground">Expérience min</span>
              <Badge variant="outline" className="ml-1">{criteria.experienceMin} ans</Badge>
            </div>
          )}
          {criteria.secteurs && criteria.secteurs.length > 0 && (
            <div>
              <span className="text-xs text-muted-foreground">Secteurs</span>
              {criteria.secteurs.map((s) => (
                <Badge key={s} variant="outline" className="ml-1">{s}</Badge>
              ))}
            </div>
          )}
          {criteria.localisation && (
            <div>
              <span className="text-xs text-muted-foreground">Localisation</span>
              <Badge variant="outline" className="ml-1">
                <MapPin className="h-3 w-3 mr-1" />
                {criteria.localisation}
              </Badge>
            </div>
          )}
          {criteria.contractType && (
            <div>
              <span className="text-xs text-muted-foreground">Contrat</span>
              <Badge variant="outline" className="ml-1">
                {criteria.contractType.toUpperCase()}
              </Badge>
            </div>
          )}
          {(criteria.budgetMin != null || criteria.budgetMax != null) && (
            <div>
              <span className="text-xs text-muted-foreground">Budget</span>
              <Badge variant="outline" className="ml-1">
                {criteria.budgetMin != null && criteria.budgetMax != null
                  ? `${criteria.budgetMin.toLocaleString()} - ${criteria.budgetMax.toLocaleString()}`
                  : criteria.budgetMin != null
                    ? `≥ ${criteria.budgetMin.toLocaleString()}`
                    : `≤ ${criteria.budgetMax!.toLocaleString()}`}
                {criteria.contractType === 'freelance' ? ' €/jour' : ' €/an'}
              </Badge>
            </div>
          )}
          {criteria.autresCriteres && criteria.autresCriteres.length > 0 && (
            <div>
              <span className="text-xs text-muted-foreground">Autres</span>
              {criteria.autresCriteres.map((a) => (
                <Badge key={a} variant="outline" className="ml-1 text-xs">{a}</Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MatchResultCard({ result, rank, hiddenCriteria }: { result: MatchingResultWithScore; rank: number; hiddenCriteria: Set<string> }) {
  const [expanded, setExpanded] = useState(false);
  const visibleCriteria = (Object.keys(ALL_CRITERIA_LABELS) as Array<keyof typeof ALL_CRITERIA_LABELS>)
    .filter((key) => !hiddenCriteria.has(key));
  const initials = `${result.firstName[0]}${result.lastName[0]}`.toUpperCase();

  const getScoreBg = (score: number): string => {
    if (score >= 70) return 'bg-green-100 border-green-200';
    if (score >= 40) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-700';
    if (score >= 40) return 'text-yellow-700';
    return 'text-red-600';
  };

  const getBarColor = (score: number): string => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-400';
  };

  return (
    <Card className={`transition-shadow ${getScoreBg(result.scoreGlobal)}`}>
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-muted-foreground w-8">#{rank}</span>
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FavoriteButton
                candidateId={result.candidateId}
                isFavorite={result.isFavorite}
              />
              <Link to={`/itc/candidates/${result.candidateId}`} className="hover:underline">
                <h3 className="font-semibold truncate">
                  {result.firstName} {result.lastName}
                </h3>
              </Link>
              {result.seniority && (
                <Badge variant="outline" className="text-xs">{result.seniority}</Badge>
              )}
              {result.primaryJobName && (
                <Badge variant="secondary" className="text-xs">{result.primaryJobName}</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              {result.currentTitle && (
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3 w-3" />
                  {result.currentTitle}
                </span>
              )}
              {(result.departementName ?? result.locationCity) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {result.departementName ?? result.locationCity}
                </span>
              )}
              {result.activityName && (
                <Badge variant="outline" className="text-xs">{result.activityName}</Badge>
              )}
            </div>
          </div>

          <div className="text-right min-w-[60px]">
            <div className={`text-2xl font-bold ${getScoreColor(result.scoreGlobal)}`}>
              {result.scoreGlobal}
            </div>
            <div className="text-xs text-muted-foreground">score</div>
          </div>
        </div>

        {/* Score detail bars — critères comptés dans la note (les exclus sont masqués) */}
        <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: `repeat(${visibleCriteria.length}, minmax(0, 1fr))` }}>
          {visibleCriteria.map((key) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">{ALL_CRITERIA_LABELS[key]}</span>
                <span className="text-[10px] font-medium">{result.scoreDetail[key as keyof typeof result.scoreDetail]}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/50">
                <div
                  className={`h-full rounded-full ${getBarColor(result.scoreDetail[key as keyof typeof result.scoreDetail])}`}
                  style={{ width: `${result.scoreDetail[key as keyof typeof result.scoreDetail]}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Expand for justification */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? 'Masquer' : 'Justification IA'}
        </button>

        {expanded && (
          <div className="mt-2 space-y-3">
            {result.synthese && (
              <p className="text-sm text-foreground">{result.synthese}</p>
            )}
            {result.pointsForts.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700">
                  <ThumbsUp className="h-3.5 w-3.5" />
                  Points forts
                </div>
                <ul className="ml-1 space-y-0.5">
                  {result.pointsForts.map((p, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-1.5">
                      <span className="text-green-600">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.pointsVigilance.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Points de vigilance
                </div>
                <ul className="ml-1 space-y-0.5">
                  {result.pointsVigilance.map((p, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-1.5">
                      <span className="text-amber-600">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.extraits.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {result.extraits.map((e, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{e}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Sélecteur de candidats pour restreindre le matching à une liste cochée.
 * Si aucune sélection, le matching s'effectue sur tous les candidats (mode par défaut).
 */
function CandidateSelector({
  selectedIds,
  onToggle,
  onClear,
}: {
  selectedIds: number[];
  onToggle: (id: number) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCandidates(
    { search: search.trim() || undefined },
    { page: 1, pageSize: 50, sortBy: 'lastName', sortOrder: 'asc' },
  );
  const candidates = data?.data ?? [];
  const hasSelection = selectedIds.length > 0;

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle className="text-base">Restreindre à une sélection de candidats</CardTitle>
            {hasSelection && (
              <Badge variant="default" className="ml-1">{selectedIds.length}</Badge>
            )}
          </div>
          {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {hasSelection
              ? `Le matching portera uniquement sur les ${selectedIds.length} candidat(s) sélectionné(s).`
              : 'Aucune sélection : le matching portera sur tous les candidats. Cochez des candidats pour le restreindre.'}
          </p>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un candidat par nom, email, titre..."
                className="pl-9"
              />
            </div>
            {hasSelection && (
              <Button variant="outline" size="sm" onClick={onClear}>
                <X className="mr-1 h-4 w-4" />
                Tout effacer
              </Button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Aucun candidat trouvé.
              </div>
            ) : (
              candidates.map((c) => {
                const checked = selectedIds.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                  >
                    <Checkbox checked={checked} onCheckedChange={() => onToggle(c.id)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {c.firstName} {c.lastName}
                        </span>
                        {c.isFavorite && (
                          <Star className="h-3 w-3 fill-current text-yellow-400 shrink-0" />
                        )}
                      </div>
                      {c.currentTitle && (
                        <span className="text-xs text-muted-foreground truncate block">
                          {c.currentTitle}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
